<script setup>
import { cloneDeep } from 'lodash'
import { computed, inject, provide, reactive, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'

import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { useCheckinModal } from '@/utils/check-ins/check-ins'
import { usePeriodGranulation, useViewPointLastDate } from '@/utils/element-progress'
import { ELEMENT_PROGRESS_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  currentModuleStateInjectionKey,
  listStateInjectionKey,
  progressViewStateInjectionKey,
  splitViewStateInjectionKey
} from '@/utils/injection-keys'
import { getSafeUpdatedElementParameters, saveUpdatedElementParameters } from '@/utils/objectives'
import { PROGRESS_VIEW_COLUMN_COUNT_OPTIONS, PROGRESS_VIEW_PLACEMENT } from '@/utils/progress-view'
import { DEFAULT_SPLIT_VIEW_STATE } from '@/utils/split-view'
import {
  PROGRESS_VIEW_COLUMN_COUNT,
  PROGRESS_VIEW_GRANULATION_ID,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'

import CheckinModal from '@/components/objectives/element-check-in/CheckinModal'
import ProgressView from '@/components/objectives/progress-view/ProgressView'
import ProgressViewColumnsSelect from '@/components/objectives/progress-view/ProgressViewColumnsSelect'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDatesNavigation from '@/components/ui/AppDatesNavigation/AppDatesNavigation'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import SplitView from '@/views/workspaces/_workspaceId/objectives/_okrElementsView/SplitView'

defineOptions({
  name: 'OkrElementsProgressPage'
})

const splitViewRef = useTemplateRef('split-view')

const requestData = expandAll => {
  splitViewRef.value.requestData(expandAll)
}

defineExpose({
  requestData
})

const emit = defineEmits({
  'load-more': null,
  'reset-search-criteria': null,
  'expand-all': null,
  'collapse-all': null,
  'update-data': null,
  'create-objective': null
})

const listState = inject(listStateInjectionKey)

const splitViewState = reactive(cloneDeep(DEFAULT_SPLIT_VIEW_STATE))

provide(splitViewStateInjectionKey, splitViewState)

const { isResetButtonDisabled, resetInitialViewPoint, viewPointLastDatesForNavigation } =
  useViewPointLastDate({
    [ELEMENT_PROGRESS_ENTITY_KEYS.PERIODS]: () => listState.progressViewState.periods || [],
    shouldStopWatcher: false,
    currentGranulationId: () => listState.progressViewState.granulationId
  })

watch(
  () => listState.progressViewState.granulationId,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && oldValue) {
      resetInitialViewPoint()
    }
  },
  { immediate: true }
)

const triggerUpdateViewPointLastDateRequestParameter = (timestamp, shouldReloadElements = true) => {
  listState.progressViewState.updateViewPointLastDateRequestParameter(
    timestamp,
    shouldReloadElements
  )
}

const moveToStartpoint = () => {
  triggerUpdateViewPointLastDateRequestParameter(null)
}

const moveBackward = () => {
  triggerUpdateViewPointLastDateRequestParameter(viewPointLastDatesForNavigation.value.backward)
}

const moveForward = () => {
  triggerUpdateViewPointLastDateRequestParameter(viewPointLastDatesForNavigation.value.forward)
}

const onUpdateColumnCount = columnCount => {
  listState.progressViewState.updateColumnCountRequestParameter(columnCount)
}

const { getPeriodGranulationOptions } = usePeriodGranulation({ pluralizeOptions: true })

const onGranulationChange = granulationId => {
  triggerUpdateViewPointLastDateRequestParameter(null, false)
  listState.progressViewState.updateGranulationRequestParameter(granulationId)
  store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[PROGRESS_VIEW_GRANULATION_ID]]: granulationId
  })
}

const store = useStore()
const fullScreen = computed(() => store.state.system.fullscreen)
const onFullscreenClick = () => {
  store.dispatch('system/toggleFullscreen')
}

const progressViewModuleState = computed(() => {
  return {
    currentGranulationId: listState.progressViewState.granulationId
  }
})

provide(currentModuleStateInjectionKey, progressViewModuleState)

// const progressViewState = ref({
//   openCheckinModal: onAddValueButtonClick,
//   onElementPeriodThresholdUpdated: payload => {
//     emit('threshold-updated', payload)
//   }
// })

const {
  isCheckinModalOpen,
  handleOpenCheckinModal,
  checkinModalLaunchData,
  isShowCheckinModal,
  onCheckinModalClosed
} = useCheckinModal()

const applyElementChanges = ({
  element = {},
  updatedElementParameters = [],
  elementUniqueId
} = {}) => {
  listState.bulkActions.clearSelectedElements()
  listState.okrElements[elementUniqueId] = {
    ...listState.okrElements[elementUniqueId],
    ...getSafeUpdatedElementParameters({ element })
  }

  saveUpdatedElementParameters(listState, updatedElementParameters)
}

const progressViewState = ref({
  openCheckinModal: handleOpenCheckinModal,
  onElementPeriodThresholdUpdated: applyElementChanges,
  onJiraIssueProgressUpdated: applyElementChanges,
  viewConfig: listState.viewConfig
})

provide(progressViewStateInjectionKey, progressViewState)
</script>

<template>
  <SplitView
    ref="split-view"
    :placement="PROGRESS_VIEW_PLACEMENT"
    class="pr-SplitView"
    show-filter-buttons
    @load-more="emit('load-more', $event)"
    @reset-search-criteria="emit('reset-search-criteria', $event)"
    @expand-all="emit('expand-all')"
    @collapse-all="emit('collapse-all')"
    @update-data="emit('update-data', $event)"
    @create-objective="emit('create-objective', $event)"
  >
    <template #content-panel="{ isNoElements }">
      <ProgressView v-show="!isNoElements" :is-no-elements show-filter-buttons />
    </template>
    <template #footer-actions="{ isNoElements }">
      <div class="pr-FooterActions">
        <template v-if="!isNoElements">
          <ProgressViewColumnsSelect
            v-if="listState.progressViewState.columnCount"
            class="pr-Controls_ColumnsSelect"
            :disabled="listState.isInfiniteLoading"
            :count="listState.progressViewState.columnCount"
            :options="PROGRESS_VIEW_COLUMN_COUNT_OPTIONS"
            :setting-key="PROGRESS_VIEW_COLUMN_COUNT"
            @update-count="onUpdateColumnCount"
          />
          <AppDatesNavigation
            :disabled="listState.isInfiniteLoading"
            :is-reset-disabled="isResetButtonDisabled"
            @move-to-startpoint="moveToStartpoint"
            @move-backward="moveBackward"
            @move-forward="moveForward"
          />
          <AppRadioGroup
            v-if="listState.progressViewState.granulationId"
            :disabled="listState.isInfiniteLoading"
            :model-value="listState.progressViewState.granulationId"
            :options="getPeriodGranulationOptions"
            name="progressView-view-switch"
            @update:model-value="onGranulationChange"
          />
        </template>

        <AppButton
          :icon="fullScreen ? 'full-screen-off-next' : 'full-screen-on-next'"
          class="pr-Controls_Button"
          data-testid="progress-view-fullscreen-button"
          remove-padding
          type="tertiary-next"
          @click="onFullscreenClick"
        />
      </div>
    </template>
  </SplitView>

  <portal to="modal-windows">
    <CheckinModal
      v-if="isShowCheckinModal"
      v-model="isCheckinModalOpen"
      :entry-point="checkinModalLaunchData.entryPoint"
      :okr-element="checkinModalLaunchData.okrElement"
      :preset-backdate="checkinModalLaunchData.presetBackdate"
      :tracking-source="EVENT_SOURCES.CUSTOM_DASHBOARD"
      :view-config="listState.viewConfig"
      @saved="applyElementChanges"
      @after-leave="onCheckinModalClosed"
    />
  </portal>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mind-road-map-footer';

.pr-SplitView {
  --pv-row-height: 64px;
  --otr-row-height: calc(var(--pv-row-height) - 1px);
}

.pr-Controls_ColumnsSelect {
  --pvc-trigger-background: #{$grey-3-next};
  --pvc-counter-size: 32px;
  --pvc-counter-padding-inline: 8px 5px;
}

.pr-Controls_Button {
  @extend %button-styles;
}

.pr-FooterActions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
